import { useEffect, useState } from "react";
import { api, fmtPrice } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Plus, ChefHat, Calculator, Save, RefreshCw, Zap, Bookmark, TrendingUp, Download, ShieldCheck, CalendarClock } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function Simulator() {
  const { user } = useAuth();
  const [presets, setPresets] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [items, setItems] = useState([{ code: "WHEAT", qty: 0.5 }]);
  const [yieldKg, setYieldKg] = useState(1.0);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({ name: "", sku: "", alert_threshold_pct: 2.0,
                                              yoy_alert_threshold_pct: 0, yoy_alert_window_days: 30 });

  const isPro = user?.tier !== "free";

  const loadSaved = async () => {
    if (!isPro) return;
    try { const { data } = await api.get("/recipes"); setSaved(data); } catch {}
  };

  useEffect(() => {
    (async () => {
      const [p, c] = await Promise.all([api.get("/simulator/presets"), api.get("/commodities")]);
      setPresets(p.data); setCommodities(c.data);
      await loadSaved();
    })();
  }, [user?.tier]); // eslint-disable-line

  const applyPreset = (key) => {
    const p = presets.find(x => x.key === key);
    if (!p) return;
    setYieldKg(p.yield_kg);
    setItems(p.items.map(i => ({ code: i.code, qty: i.qty })));
    setResult(null);
  };

  const loadSavedRecipe = (r) => {
    setYieldKg(r.yield_kg);
    setItems(r.items.map(i => ({ code: i.code, qty: i.qty })));
    setResult(null);
    setSaveForm({ name: r.name, sku: r.sku || "", alert_threshold_pct: r.alert_threshold_pct,
                   yoy_alert_threshold_pct: r.yoy_alert_threshold_pct ?? 0,
                   yoy_alert_window_days: r.yoy_alert_window_days ?? 30 });
    toast.success(`Loaded "${r.name}"`);
  };

  const addRow = () => setItems([...items, { code: "SUGAR", qty: 0.01 }]);
  const removeRow = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateRow = (idx, field, val) => setItems(items.map((r, i) => i === idx ? { ...r, [field]: field === "qty" ? parseFloat(val) || 0 : val } : r));

  const compute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/simulator/cost", { yield_kg: yieldKg, items });
      setResult(data);
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const saveRecipe = async () => {
    try {
      await api.post("/recipes", { ...saveForm, yield_kg: yieldKg, items });
      toast.success(`Saved "${saveForm.name}"`);
      setSaveOpen(false);
      await loadSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const rebase = async (id) => {
    await api.post(`/recipes/${id}/rebase`);
    toast.success("Baseline reset to current cost");
    await loadSaved();
  };

  const forceCheck = async (id) => {
    try {
      const { data } = await api.post(`/recipes/${id}/force-check`);
      const status = data.email?.sent ? "sent" : data.email?.queued ? "queued (Resend not configured)" : "failed";
      toast.success(`Recipe alert fired · email ${status}`);
      await loadSaved();
    } catch { toast.error("Failed"); }
  };

  const forceYoy = async (id, days = 30) => {
    try {
      const { data } = await api.post(`/recipes/${id}/force-yoy?days=${days}`);
      const status = data.email?.sent ? "sent" : data.email?.queued ? "queued" : "failed";
      toast.success(`YoY alert fired · ${data.yoy_pct >= 0 ? "+" : ""}${data.yoy_pct}% · cover ${data.cover.weeks}w · email ${status}`);
      await loadSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteRecipe = async (id) => {
    await api.delete(`/recipes/${id}`);
    toast.success("Deleted");
    await loadSaved();
  };

  const [pnlRecipe, setPnlRecipe] = useState(null);
  const [pnlData, setPnlData] = useState(null);
  const [pnlDays, setPnlDays] = useState(7);
  const [coverData, setCoverData] = useState(null);
  const openPnl = async (r, days = 7) => {
    setPnlRecipe(r); setPnlData(null); setPnlDays(days); setCoverData(null);
    try {
      const [pnlRes, coverRes] = await Promise.all([
        api.get(`/recipes/${r.id}/history?days=${days}`),
        api.get(`/recipes/${r.id}/cover?days=${days}`).catch(() => ({ data: null })),
      ]);
      setPnlData(pnlRes.data);
      setCoverData(coverRes.data);
    } catch { toast.error("Failed"); }
  };

  const downloadPnlCsv = async () => {
    if (!pnlRecipe) return;
    try {
      const { data } = await api.get(`/recipes/${pnlRecipe.id}/pnl.csv?days=${pnlDays}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const safe = (pnlRecipe.name || "recipe").replace(/[^A-Za-z0-9_-]+/g, "_");
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safe}_${pnlDays}d_pnl.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PnL CSV downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-primary flex items-center gap-2"><ChefHat className="h-3 w-3" /> Simulator</div>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Recipe cost simulator</h1>
        <p className="mt-1 text-sm text-muted-foreground">Input a recipe and see per-unit cost swings as underlying commodity markets move. Preset templates for bakery, pizza, burger.</p>
      </div>

      <Card className="border-border/60 bg-card/60">
        <CardHeader><CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground">Presets</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {presets.map(p => (
              <Button key={p.key} size="sm" variant="outline" data-testid={`preset-${p.key}`} onClick={() => applyPreset(p.key)} className="rounded-full">{p.label}</Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/60 bg-card/60">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground">Ingredients</CardTitle>
          <div className="flex items-center gap-2">
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Yield (kg)</label>
            <Input type="number" step="0.01" value={yieldKg} onChange={e => setYieldKg(parseFloat(e.target.value) || 0)} data-testid="yield-input" className="w-24" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {items.map((it, idx) => (
              <div key={idx} data-testid={`ingredient-${idx}`} className="grid grid-cols-[1.5fr_1fr_auto] items-center gap-2">
                <Select value={it.code} onValueChange={v => updateRow(idx, "code", v)}>
                  <SelectTrigger data-testid={`ingredient-code-${idx}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{commodities.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" step="0.001" value={it.qty} onChange={e => updateRow(idx, "qty", e.target.value)} placeholder="kg" data-testid={`ingredient-qty-${idx}`} />
                <Button size="icon" variant="ghost" onClick={() => removeRow(idx)}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
              </div>
            ))}
            <Button size="sm" variant="ghost" onClick={addRow} data-testid="add-ingredient" className="mt-2"><Plus className="mr-1 h-3 w-3" />Add ingredient</Button>
          </div>
          <Button className="mt-4 rounded-full" onClick={compute} disabled={busy} data-testid="compute-btn"><Calculator className="mr-2 h-4 w-4" />{busy ? "Computing…" : "Compute cost"}</Button>
          {isPro && (
            <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
              <DialogTrigger asChild>
                <Button className="ml-2 mt-4 rounded-full" variant="outline" data-testid="save-recipe-btn">
                  <Save className="mr-2 h-4 w-4" />Save recipe
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display">Save recipe</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Recipe name</label>
                    <Input data-testid="save-name" value={saveForm.name} onChange={e => setSaveForm({...saveForm, name: e.target.value})} placeholder="e.g. Sourdough Loaf 800g" />
                  </div>
                  <div>
                    <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Your SKU (optional)</label>
                    <Input data-testid="save-sku" value={saveForm.sku} onChange={e => setSaveForm({...saveForm, sku: e.target.value})} placeholder="e.g. SDL-800-01" />
                  </div>
                  <div>
                    <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Alert when cost drifts ±</label>
                    <div className="flex items-center gap-2">
                      <Input type="number" step="0.5" data-testid="save-threshold" value={saveForm.alert_threshold_pct} onChange={e => setSaveForm({...saveForm, alert_threshold_pct: parseFloat(e.target.value) || 2})} className="w-24" />
                      <span className="text-sm">%</span>
                    </div>
                  </div>
                  <div className="rounded-md border border-border/60 bg-background/40 p-3">
                    <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">YoY (Season-over-Season) alert</label>
                    <p className="mt-1 text-[11px] text-muted-foreground">Fire when |YoY delta| crosses this threshold. Set 0 to disable.</p>
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Input type="number" step="0.5" min="0" data-testid="save-yoy-threshold" value={saveForm.yoy_alert_threshold_pct} onChange={e => setSaveForm({...saveForm, yoy_alert_threshold_pct: Math.max(0, parseFloat(e.target.value) || 0)})} className="w-24" />
                        <span className="text-sm">% YoY</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Window</span>
                        <Select value={String(saveForm.yoy_alert_window_days)} onValueChange={(v) => setSaveForm({...saveForm, yoy_alert_window_days: parseInt(v, 10)})}>
                          <SelectTrigger data-testid="save-yoy-window" className="h-9 w-24"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[7, 14, 30, 90].map(d => <SelectItem key={d} value={String(d)}>{d}d</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
                  <Button data-testid="save-submit" onClick={saveRecipe}>Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </CardContent>
      </Card>

      {result && (
        <Card data-testid="sim-result" className="border-primary/30 bg-primary/[0.03]">
          <CardHeader><CardTitle className="text-sm font-mono uppercase tracking-widest text-primary">Cost breakdown</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Total</div>
                <div className="num text-2xl font-bold">${result.total_cost_usd.toFixed(3)}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Yield</div>
                <div className="num text-2xl font-bold">{result.yield_kg} kg</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">$/kg output</div>
                <div className="num text-2xl font-bold text-primary">${result.per_kg_usd.toFixed(3)}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Per unit</div>
                <div className="num text-2xl font-bold">${(result.total_cost_usd).toFixed(3)}</div>
              </div>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  <tr className="border-b border-border/60">
                    <th className="py-2 text-left">Ingredient</th>
                    <th className="py-2 text-right">Qty (kg)</th>
                    <th className="py-2 text-right">Spot</th>
                    <th className="py-2 text-right">$/kg</th>
                    <th className="py-2 text-right">Line cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {result.lines.map((l, i) => (
                    <tr key={i} data-testid={`line-${l.code}`}>
                      <td className="py-2">{l.name} <span className="text-muted-foreground">({l.code})</span></td>
                      <td className="py-2 text-right num">{l.qty_kg.toFixed(3)}</td>
                      <td className="py-2 text-right num text-muted-foreground">{l.unit_price ? fmtPrice(l.unit_price) : "—"} {l.native_unit}</td>
                      <td className="py-2 text-right num">{l.dollars_per_kg ? "$"+l.dollars_per_kg.toFixed(2) : "—"}</td>
                      <td className="py-2 text-right num font-semibold">{l.line_cost_usd != null ? "$"+l.line_cost_usd.toFixed(4) : <Badge variant="outline" className="text-[10px]">unit-priced</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {result.unpriced_codes.length > 0 && (
              <p className="mt-3 text-[11px] text-muted-foreground">Note: {result.unpriced_codes.join(", ")} are unit-priced (per carton / dozen) and require manual conversion for now.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Saved recipes with drift alerts */}
      {isPro && (
        <Card className="border-border/60 bg-card/60">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2"><Bookmark className="h-4 w-4" />Saved recipes · drift alerts</CardTitle>
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{saved.length} saved</span>
          </CardHeader>
          <CardContent>
            {saved.length === 0 ? (
              <div className="rounded-md border border-dashed border-border/60 p-8 text-center text-sm text-muted-foreground">
                Save any computed recipe with a name + SKU. AgriTerminal will notify you when the per-kg cost drifts more than your chosen threshold vs the baseline you saved.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    <tr className="border-b border-border/60">
                      <th className="py-2 text-left">Recipe</th>
                      <th className="py-2 text-right">Baseline $/kg</th>
                      <th className="py-2 text-right">Current $/kg</th>
                      <th className="py-2 text-right">Drift</th>
                      <th className="py-2 text-right">Threshold</th>
                      <th className="py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {saved.map(r => (
                      <tr key={r.id} data-testid={`saved-${r.id}`} className="align-top">
                        <td className="py-3">
                          <div className="font-semibold">{r.name}</div>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{r.sku || "no SKU"} · yield {r.yield_kg} kg</div>
                        </td>
                        <td className="py-3 text-right num">${r.baseline_per_kg_usd?.toFixed(4)}</td>
                        <td className="py-3 text-right num font-semibold">${r.current_per_kg_usd?.toFixed(4)}</td>
                        <td className="py-3 text-right">
                          <span className={`num font-semibold ${r.drift_pct > 0 ? "text-[hsl(var(--signal-wait))]" : r.drift_pct < 0 ? "text-[hsl(var(--signal-buy))]" : "text-muted-foreground"}`}>
                            {r.drift_pct >= 0 ? "+" : ""}{r.drift_pct?.toFixed(2)}%
                          </span>
                          {r.alert_armed && <Badge variant="outline" className="ml-2 uppercase text-[10px] signal-wait border">Armed</Badge>}
                        </td>
                        <td className="py-3 text-right num text-muted-foreground">±{r.alert_threshold_pct}%</td>
                        <td className="py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" data-testid={`load-${r.id}`} onClick={() => loadSavedRecipe(r)} title="Load into editor"><ChefHat className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" data-testid={`pnl-${r.id}`} onClick={() => openPnl(r)} title="7-day PnL"><TrendingUp className="h-4 w-4 text-primary" /></Button>
                            <Button size="sm" variant="ghost" data-testid={`rebase-${r.id}`} onClick={() => rebase(r.id)} title="Reset baseline to current cost"><RefreshCw className="h-4 w-4 text-muted-foreground" /></Button>
                            <Button size="sm" variant="ghost" data-testid={`force-check-${r.id}`} onClick={() => forceCheck(r.id)} title="Simulate drift alert"><Zap className="h-4 w-4 text-[hsl(var(--signal-hold))]" /></Button>
                            <Button size="sm" variant="ghost" data-testid={`force-yoy-${r.id}`} onClick={() => forceYoy(r.id, r.yoy_alert_window_days || 30)} title="Force YoY alert (with Cover recommendation)"><CalendarClock className="h-4 w-4 text-primary" /></Button>
                            <Button size="sm" variant="ghost" data-testid={`delete-${r.id}`} onClick={() => deleteRecipe(r.id)}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* PnL dialog */}
      <Dialog open={!!pnlRecipe} onOpenChange={(o) => { if (!o) { setPnlRecipe(null); setPnlData(null); setCoverData(null); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><TrendingUp className="h-5 w-5 text-primary" />{pnlDays}-day PnL · {pnlRecipe?.name}</DialogTitle>
          </DialogHeader>
          {/* Timeframe toggle */}
          <div className="flex flex-wrap items-center gap-1">
            {[7, 14, 30, 90].map(d => (
              <button
                key={d}
                data-testid={`pnl-tf-${d}`}
                onClick={() => openPnl(pnlRecipe, d)}
                className={`rounded-md px-3 py-1 text-xs font-mono transition ${pnlDays === d ? "bg-primary text-primary-foreground" : "border border-border/60 text-muted-foreground hover:text-foreground"}`}
              >
                {d}d
              </button>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={downloadPnlCsv}
              disabled={!pnlData}
              data-testid="pnl-csv-download"
              className="ml-auto gap-1.5 text-xs"
              title="Download PnL as CSV for Excel"
            >
              <Download className="h-3.5 w-3.5" />
              Download CSV
            </Button>
          </div>
          {!pnlData && <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>}
          {pnlData && (
            <div className="space-y-4">
              <div className="h-56" data-testid="pnl-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={pnlData.days} margin={{top:10,right:10,left:0,bottom:0}}>
                    <defs>
                      <linearGradient id="pnl-grad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4}/>
                        <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" opacity={0.35}/>
                    <XAxis dataKey="date" tick={{fontSize:10, fill:"hsl(var(--muted-foreground))"}} />
                    <YAxis tick={{fontSize:10, fill:"hsl(var(--muted-foreground))"}} width={70} tickFormatter={v => `$${v.toFixed(3)}`} />
                    <Tooltip contentStyle={{background:"hsl(var(--card))", border:"1px solid hsl(var(--border))", fontSize:12}} formatter={(v) => v!=null ? `$${Number(v).toFixed(4)}` : "—"} />
                    <Area type="monotone" dataKey="per_kg_usd_yoy" name="Last year" stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} strokeDasharray="4 3" fill="none" isAnimationActive={false} connectNulls />
                    <Area type="monotone" dataKey="per_kg_usd" name="Current" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#pnl-grad)"/>
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {pnlData.yoy_summary && (
                <div className={`rounded-md border p-3 ${pnlData.yoy_summary.delta_pct >= 0 ? "border-[hsl(var(--signal-wait))]/40 bg-[hsl(var(--signal-wait))]/5" : "border-[hsl(var(--signal-buy))]/40 bg-[hsl(var(--signal-buy))]/5"}`} data-testid="yoy-summary">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">vs same window last year</div>
                  <div className="mt-1 flex flex-wrap items-baseline gap-3">
                    <span className="font-display text-lg font-semibold">
                      ${pnlData.yoy_summary.current_avg_per_kg.toFixed(4)}/kg
                    </span>
                    <span className="text-xs text-muted-foreground">now vs</span>
                    <span className="font-mono text-sm text-muted-foreground line-through">
                      ${pnlData.yoy_summary.year_ago_avg_per_kg.toFixed(4)}/kg
                    </span>
                    <span className="text-xs text-muted-foreground">a year ago</span>
                    <Badge variant="outline" className={`ml-auto uppercase text-[10px] ${pnlData.yoy_summary.delta_pct >= 0 ? "signal-wait border" : "signal-buy border"}`}>
                      {pnlData.yoy_summary.delta_pct >= 0 ? "+" : ""}{pnlData.yoy_summary.delta_pct.toFixed(2)}% YoY
                    </Badge>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {pnlData.yoy_summary.delta_pct >= 5
                      ? `You're paying materially more per kg than the same ${pnlDays} days last year — worth reviewing forward cover.`
                      : pnlData.yoy_summary.delta_pct <= -5
                      ? `You're paying materially less per kg than the same ${pnlDays} days last year — good window to lock forward volume.`
                      : `Broadly in line with the same ${pnlDays} days last year.`}
                  </p>
                </div>
              )}

              {coverData && (
                <div className="rounded-md border border-[hsl(var(--signal-buy))]/40 bg-[hsl(var(--signal-buy))]/5 p-3" data-testid="cover-recommendation">
                  <div className="flex flex-wrap items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-[hsl(var(--signal-buy))]" />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[hsl(var(--signal-buy))]">Cover recommendation</span>
                    <Badge variant="outline" className="ml-auto uppercase text-[10px]" data-testid="cover-weeks">
                      {coverData.weeks} weeks · {coverData.confidence}% conf
                    </Badge>
                  </div>
                  <div className="mt-1 font-display text-base font-semibold" data-testid="cover-headline">{coverData.headline}</div>
                  <p className="mt-1 text-xs text-muted-foreground" data-testid="cover-rationale">{coverData.rationale}</p>
                  {coverData.vol_ingredients?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {coverData.vol_ingredients.map(v => (
                        <span key={v.code} className="rounded-full border border-border/60 bg-background/60 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                          {v.name} · vol {v.vol_pct_30d}%
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {pnlData.top_mover && (
                <div className="rounded-md border border-primary/40 bg-primary/5 p-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-primary">Top mover · past {pnlDays} days</div>
                  <div className="mt-1 font-display text-base font-semibold" data-testid="top-mover">
                    {pnlData.top_mover.name}
                    <span className={`ml-2 num text-sm ${pnlData.top_mover.delta_pct >= 0 ? "text-[hsl(var(--signal-wait))]" : "text-[hsl(var(--signal-buy))]"}`}>
                      {pnlData.top_mover.delta_pct >= 0 ? "+" : ""}{pnlData.top_mover.delta_pct}% contribution
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Contributed <span className="num">${pnlData.top_mover.start_usd.toFixed(4)}</span> {pnlDays} days ago →
                    <span className="num"> ${pnlData.top_mover.end_usd.toFixed(4)}</span> today.
                    {pnlData.top_mover.delta_pct >= 0
                      ? " This is the ingredient that pushed your recipe cost higher over this window."
                      : " This is the ingredient that pulled your recipe cost lower over this window."}
                  </p>
                </div>
              )}

              <div>
                <div className="mb-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">All ingredient contributions</div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {pnlData.contributions.map(c => (
                    <div key={c.code} data-testid={`contribution-${c.code}`} className="flex items-center justify-between rounded-md border border-border/60 bg-background/50 p-2 text-xs">
                      <span className="font-semibold">{c.name}</span>
                      <span className={`num ${c.delta_pct >= 0 ? "text-[hsl(var(--signal-wait))]" : "text-[hsl(var(--signal-buy))]"}`}>
                        {c.delta_pct >= 0 ? "+" : ""}{c.delta_pct}%
                        <span className="ml-2 text-muted-foreground">${c.delta_usd >= 0 ? "+" : ""}{c.delta_usd.toFixed(4)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
